import { apiService } from './apiService';
import { IngredientCatalog } from '../models/diary';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface IngredientPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IngredientCatalog[];
  totalPages: number;
  actualPage: number;
}

// Nutrition values are per 100g. `name` and `calories` are required.
export interface CreateIngredientData {
  name: string;
  calories: number;
  carbohydrates?: number;
  proteins?: number;
  fats?: number;
  water?: number | null;
  fiber?: number;
}

export const ingredientService = {
  getAll: async (page: number = 1, perpage: number = 10, search: string = ''): Promise<IngredientPaginatedResponse> => {
    const params = new URLSearchParams({ page: String(page), perpage: String(perpage) });
    if (search.trim()) params.set('search', search.trim());
    const url = `${API_BASE_URL}/clinic/ingredients/?${params.toString()}`;
    try {
      const response = await apiService.get(url);
      return {
        ...response,
        actualPage: page,
        totalPages: Math.ceil(response.count / perpage) || 1,
      };
    } catch (error) {
      console.error('Error fetching ingredients:', error);
      throw error;
    }
  },

  getOne: async (id: number): Promise<IngredientCatalog> => {
    const url = `${API_BASE_URL}/clinic/ingredients/${id}/`;
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error(`Error fetching ingredient ${id}:`, error);
      throw error;
    }
  },

  create: async (data: CreateIngredientData): Promise<IngredientCatalog> => {
    const url = `${API_BASE_URL}/clinic/ingredients/`;
    try {
      const response = await apiService.post(url, data);
      return response;
    } catch (error) {
      console.error('Error creating ingredient:', error);
      throw error;
    }
  },

  update: async (id: number, data: Partial<CreateIngredientData>): Promise<IngredientCatalog> => {
    const url = `${API_BASE_URL}/clinic/ingredients/${id}/`;
    try {
      const response = await apiService.patch(url, data);
      return response;
    } catch (error) {
      console.error(`Error updating ingredient ${id}:`, error);
      throw error;
    }
  },

  // 204 if not used by any meal; 409 with { error } if in use.
  remove: async (id: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/ingredients/${id}/`;
    try {
      await apiService.delete(url);
    } catch (error) {
      console.error(`Error deleting ingredient ${id}:`, error);
      throw error;
    }
  },
};
